/**
 * The agent's loopback api. `StatusResponse` and `PairingScreen` are agent-local shapes with no
 * sdk contract and no generated TypeScript, so both are declared by hand below.
 */

import { agentBaseUrl } from "@/lib/env";

export const AGENT_UNREACHABLE = "agent_unreachable";

/**
 * The browser is holding the request while it asks whether this page may reach the machine.
 *
 * Chrome 152 gates a public page reaching 127.0.0.1 behind a Local Network Access permission, and
 * an unanswered prompt does not fail the fetch: it hangs it. Without a deadline every agent screen
 * sits on a spinner forever, which reads as an agent that is not running while the agent is
 * running perfectly well and answering everything else.
 */
export const AGENT_PERMISSION = "agent_permission";

/** Long enough for a loopback answer, short enough that a pending prompt is not a hang. */
const TIMEOUT_MS = 4_000;

const LOCAL_NETWORK = "local-network-access" as PermissionName;

/** "prompt" or "denied" while the browser is standing between this page and the machine. */
async function localNetworkPermission(): Promise<PermissionState | null> {
  try {
    return (await navigator.permissions.query({ name: LOCAL_NETWORK })).state;
  } catch {
    return null;
  }
}

export class AgentError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number = 0,
  ) {
    super(message);
    this.name = "AgentError";
  }
}

/** True when the process did not answer at all, rather than refusing something. */
export function isUnreachable(error: unknown): boolean {
  if (!(error instanceof AgentError)) return false;
  return error.code === AGENT_UNREACHABLE || error.status >= 500;
}

/** True when the browser, not the agent, is what stands in the way. */
export function needsLocalNetworkPermission(error: unknown): boolean {
  return error instanceof AgentError && error.code === AGENT_PERMISSION;
}

/** `GazeMode` in `agent/core/settings.py`. `replay` is the synthetic source. */
export type GazeMode = "tobii" | "replay";

/** `ControlPlaneState.status` in `agent/adapters/cloud/client.py`. */
export type CloudStatus = "connected" | "reconnecting" | "offline" | "unpaired";

/** `ControlPlaneState.snapshot()`, which reaches `cloud` as a plain dict. */
export interface CloudSnapshot {
  readonly status?: string;
  readonly connection_id?: string | null;
  readonly space_id?: string | null;
  readonly retry_at?: string | null;
  readonly attempts?: number;
  readonly reason?: string | null;
  readonly handshakes?: number;
  readonly sent?: number;
  readonly acknowledged?: number;
  readonly projections?: number;
  readonly applied?: number;
}

/** `StatusResponse` in `agent/server/app.py`. Agent-local diagnostics. */
export interface StatusResponse {
  readonly status: string;
  readonly environment: string;
  readonly gaze_mode: GazeMode | null;
  readonly tracker: string | null;
  readonly gaze_samples_published: number;
  readonly events_published: number;
  readonly bus_subscribers: number;
  readonly mirror_status: string | null;
  readonly mirror_activities: number;
  readonly mirror_entities: number;
  readonly handler_invocations: number;
  readonly submissions_pending: number;
  readonly paired: boolean;
  readonly cloud: CloudSnapshot | null;
  readonly clock_offset_ms: number;
  readonly clock_synced: boolean;
  readonly uptime_seconds: number;
  readonly overlay_open: boolean;
  readonly overlay_click_through: boolean;
  readonly overlay_monitor: number;
  readonly overlay_rect: readonly [number, number, number, number] | null;
  readonly overlay_blocking: readonly string[];
  readonly overlay_detail: Record<string, unknown>;
}

/** `PairingPhase` in `agent/identity/pairing.py`. */
export type PairingPhase =
  | "unpaired"
  | "waiting"
  | "awaiting_confirmation"
  | "paired"
  | "expired"
  | "declined";

/** `PairingScreen` in `agent/identity/pairing.py`. It has no device code field. */
export interface PairingScreen {
  readonly phase: PairingPhase;
  readonly user_code: string | null;
  readonly code_visible: boolean;
  readonly expires_at: string | null;
  readonly approved_by: string | null;
  readonly machine_id: string | null;
  readonly label: string | null;
  readonly fingerprint: string | null;
  readonly credential_protected: boolean;
}

interface RefusalBody {
  readonly code?: string;
  readonly message?: string;
}

function asRefusal(value: unknown): RefusalBody {
  return typeof value === "object" && value !== null
    ? (value as RefusalBody)
    : {};
}

/** A refusal arrives as a 409 carrying a code and a message. */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${agentBaseUrl}${path}`, {
      ...init,
      signal: AbortSignal.timeout(TIMEOUT_MS),
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        ...init.headers,
      },
    });
  } catch {
    const permission = await localNetworkPermission();
    if (permission === "prompt" || permission === "denied") {
      throw new AgentError(
        AGENT_PERMISSION,
        permission === "denied"
          ? "This browser is blocking pages from reaching this machine."
          : "This browser is asking whether this page may reach this machine.",
      );
    }
    throw new AgentError(AGENT_UNREACHABLE, "Agent not answering");
  }

  if (!response.ok) {
    const body = asRefusal(await response.json().catch(() => null));
    throw new AgentError(
      body.code ?? AGENT_UNREACHABLE,
      body.message ?? response.statusText,
      response.status,
    );
  }

  return (await response.json()) as T;
}

function post<T>(path: string): Promise<T> {
  return request<T>(path, { method: "POST", body: "{}" });
}

export const agentApi = {
  /**
   * One read, meant to be called from a click.
   *
   * A permission prompt raised by a fetch nobody asked for is a prompt nobody reads. Asking again
   * from a button press puts the browser's question next to the person's own decision to connect.
   */
  connect: () => request<StatusResponse>("/status"),
  status: () => request<StatusResponse>("/status"),
  pairing: () => request<PairingScreen>("/pairing"),
  begin: () => post<PairingScreen>("/pairing"),
  reveal: () => post<PairingScreen>("/pairing/reveal"),
  poll: () => post<PairingScreen>("/pairing/poll"),
  confirm: () => post<PairingScreen>("/pairing/confirm"),
  decline: () => post<PairingScreen>("/pairing/decline"),
  unpair: () => post<PairingScreen>("/pairing/unpair"),
};
